import { Box, Checkbox, FormGroup, FormSection, Link, Text } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IService } from '@common/interfaces/';
import { ManagerAuthorization, ServiceCode } from '@common/enums/';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization } from '@frontend/utils';
import { useAbortableFetch } from '@frontend/utils/hooks/useAbortableFetch';
import type { IEmbloomQuestionnaire } from '../../../../interfaces/embloomQuestionnaire';

export interface IEmbloomQuestionnairesProps {
    onChange: (selectedEmbloomQuestionnaires: string[]) => void;
    selectedEmbloomQuestionnaires?: string[];
    service: IService;
}

export function EmbloomQuestionnaires(props: IEmbloomQuestionnairesProps) {
    assertHasAuthorization([ManagerAuthorization.organizationAddEdit, ManagerAuthorization.employmentSettingsEdit]);

    if (props.service.code !== ServiceCode.GGZOembl) {
        return null;
    }

    const abortableFetch = useAbortableFetch();
    const [embloomQuestionnaires, setEmbloomQuestionnaires] = useState<IEmbloomQuestionnaire[]>([]);

    useEffect(() => {
        loadEmbloomQuestionnaires();
    }, []);

    useEffect(() => {
        if (embloomQuestionnaires.length > 0 && !props.selectedEmbloomQuestionnaires) {
            setToEmbloomDefaultSet();
        }
    }, [embloomQuestionnaires]);

    return (
        <FormSection title={translateKey('embloomQuestionnaires.name')}>
            <FormGroup label={translateKey('embloomQuestionnaires.isDecisionSupport')}>
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                >
                    {getActiveQuestionnaires('decisionSupport').map((questionnaire) =>
                        renderEmbloomQuestionnaireCheckbox(props.selectedEmbloomQuestionnaires, questionnaire)
                    )}
                </Box>
            </FormGroup>
            <FormGroup label={translateKey('embloomQuestionnaires.otherQuestionnaires')}>
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                >
                    {getActiveQuestionnaires('other').map((questionnaire) =>
                        renderEmbloomQuestionnaireCheckbox(props.selectedEmbloomQuestionnaires, questionnaire)
                    )}
                </Box>
            </FormGroup>
            <Text>
                <Link
                    fontWeight="medium"
                    onClick={() => setToEmbloomDefaultSet()}
                    variant="button"
                >
                    {translateKey('embloomQuestionnaires.setToDefaultSet')}
                </Link>
            </Text>
        </FormSection>
    );

    function renderEmbloomQuestionnaireCheckbox(
        selectedEmbloomQuestionnaires: string[] | undefined,
        questionnaire: IEmbloomQuestionnaire
    ) {
        return (
            <Checkbox
                checked={Boolean(selectedEmbloomQuestionnaires?.includes(questionnaire.id))}
                label={`${questionnaire.label}: ${questionnaire.label}`}
                onChange={(e) =>
                    handleEmbloomQuestionnairesChange(selectedEmbloomQuestionnaires, e.target.checked, questionnaire.id)
                }
            />
        );
    }

    function getActiveQuestionnaires(type: 'decisionSupport' | 'other') {
        const isDecisionSupport = type === 'decisionSupport';

        return embloomQuestionnaires.filter((item) => item.isDecisionSupport === isDecisionSupport && item.isActive);
    }

    function handleEmbloomQuestionnairesChange(
        selectedEmbloomQuestionnaires: string[] | undefined,
        checked: boolean,
        questionnaireId: string
    ) {
        const selectedQuestionnaires =
            selectedEmbloomQuestionnaires === undefined ? [] : [...selectedEmbloomQuestionnaires];

        if (checked) {
            selectedQuestionnaires.push(questionnaireId);
        } else {
            selectedQuestionnaires.splice(selectedQuestionnaires.indexOf(questionnaireId), 1);
        }

        props.onChange(selectedQuestionnaires);
    }

    function setToEmbloomDefaultSet() {
        const defaultSelectedQuestionnaires = embloomQuestionnaires
            .filter((item) => item.isStandard && item.isActive)
            .map((item) => item.id);
        props.onChange(defaultSelectedQuestionnaires);
    }

    async function loadEmbloomQuestionnaires() {
        const resultQuestionnaires = await abortableFetch.fetchJson('/api/embloomQuestionnaires', 'GET');
        setEmbloomQuestionnaires(resultQuestionnaires.json.items);
    }
}
